'use no memo';

import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useMemo } from 'react';
import { Card } from '@/components/Card/card';
import { DataTable, DataTablePagination } from '@/components/DataTable';
import Icon, { IconSize } from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { TaskListTabs } from '../-components/TaskListTabs';
import { NoOperationTasksMessage } from './-components/NoOperationTasksMessage';
import { OperationTaskTab } from './-constants';
import { useOperationTasksTable } from './-hooks/useTable';
import { SearchSchema } from './-zod-schema';
export const Route = createFileRoute('/_individual/individual/_tasks/operation-tasks/')({
  validateSearch: zodValidator(SearchSchema),
  component: OperationTasksPage
});
function OperationTasksPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.operationTasks'
  });
  useDocumentTitle(t('pageTitle'));
  const {
    table,
    displayState,
    totalCount,
    tabCounts,
    onRowClick
  } = useOperationTasksTable({
    emptyContent: <NoOperationTasksMessage />
  });
  const tabs = useMemo(() => [{
    value: OperationTaskTab.IN_PROGRESS,
    label: t('tabs.inProgress'),
    count: tabCounts[OperationTaskTab.IN_PROGRESS]
  }, {
    value: OperationTaskTab.NOT_STARTED,
    label: t('tabs.beforeStart'),
    count: tabCounts[OperationTaskTab.NOT_STARTED]
  }, {
    value: OperationTaskTab.WAITING_APPROVAL,
    label: t('tabs.waitingApproval'),
    count: tabCounts[OperationTaskTab.WAITING_APPROVAL]
  }, {
    value: OperationTaskTab.COMPLETED,
    label: t('tabs.completed'),
    count: tabCounts[OperationTaskTab.COMPLETED]
  }, {
    value: OperationTaskTab.ALL,
    label: t('tabs.all'),
    count: tabCounts[OperationTaskTab.ALL]
  }], [t, tabCounts]);
  return <div className="flex h-full min-w-0 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center gap-3 border-b border-neutral-muted-border bg-neutral-fill px-5 py-3 shadow-sm md:px-12 md:py-4">
        <span className="text-neutral-on-fill">
          <Icon iconName="ListCheckIcon" size={IconSize.XL} inheritColor />
        </span>
        <Text as="h1" variant="h3">
          {t('pageTitle')}
        </Text>
      </div>
      <VStack className="min-h-0 flex-1 gap-4 px-5 py-6 md:px-12 md:py-8">
        <div className="shrink-0 overflow-clip rounded-b-[28px] border border-t-0 border-neutral-muted-border bg-neutral-fill/60 p-5 md:overflow-visible md:rounded-none md:border-0 md:bg-transparent md:p-0">
          <HStack className="items-center gap-2">
            <TaskListTabs value={search.taskTab} tabs={tabs} onChange={taskTab => navigate({
            search: prev => ({
              ...prev,
              taskTab,
              page: 1
            })
          })} />
          </HStack>
        </div>
        <Card className="min-h-0 flex-1 overflow-auto">
          <DataTable table={table} displayState={displayState} isHoverable onRowClick={onRowClick} />
        </Card>
        <DataTablePagination table={table} totalCount={totalCount} pageOptions={[10, 20, 50, 100]} />
      </VStack>
    </div>;
}